import { useMemo } from "react";
import { isMarketingLangCode } from "i18n/urlLocale";
import { useMarketingLang } from "i18n/useMarketingLang";
import Footer from "../components/Footer";
import LanguageSwitcher from "../components/LanguageSwitcher";
import Navigation from "../components/navigation/Navigation";
import "../css/AssistantTravelPlanner.scss";
import "../css/Website.scss";
import { createPageCtx } from "./assistantTravelPlanner/ctx";
import { Assistant } from "./assistantTravelPlanner/data";
import Hero from "./assistantTravelPlanner/Hero";
import LocaleJsonLd from "./assistantTravelPlanner/LocaleJsonLd";
import { SMarkSprite } from "./assistantTravelPlanner/icons";
import PromptBuilder from "./assistantTravelPlanner/PromptBuilder";
import Setup from "./assistantTravelPlanner/Setup";
import Spar from "./assistantTravelPlanner/Spar";
import { Destinations, Faq, FinalCta, Together } from "./assistantTravelPlanner/Sections";

interface AssistantTravelPlannerPageProps {
	/** Which assistant the page is for: the same page serves /chatgpt-travel-planner and /claude-travel-planner. */
	assistant?: Assistant;
	lang?: string;
}

/**
 * Landing page for "plan in ChatGPT / Claude, save it to Stippl". One component for both assistants: the Claude
 * page only swaps the name, the chat chrome, the setup steps and the links (see the `chatgpt.*` / `claude.*`
 * keys of the `assistantTravelPlanner` i18n namespace, and `.atp-page--claude` in the stylesheet).
 *
 * Server-rendered in full (every section's text, all route cards and the whole country grid); only the
 * animations and interactions start after mount.
 */
const AssistantTravelPlannerPage = ({ assistant = "chatgpt", lang }: AssistantTravelPlannerPageProps) => {
	// MarketingShell passes `lang`; the SPA route does not, so fall back to the one read from the URL.
	const urlLang = useMarketingLang();
	const pageLang = lang || urlLang;
	const ctx = useMemo(() => createPageCtx(assistant, pageLang), [assistant, pageLang]);

	return (
		<>
			{/* The site chrome sits outside the page root so the page's element-level styles (a, img, p, ...) cannot reach it. */}
			<Navigation impact={true} langMenu={<LanguageSwitcher type="page" />} />
			<div className={`atp-page atp-page--${assistant}`} id="stippl-assistant-planner">
				<SMarkSprite />
				{isMarketingLangCode(pageLang) && pageLang !== "en" && <LocaleJsonLd ctx={ctx} lang={pageLang} />}
				<main>
					<Hero ctx={ctx} />
					<Spar ctx={ctx} />
					<Together ctx={ctx} />
					<PromptBuilder ctx={ctx} />
					<Destinations ctx={ctx} />
					<Setup ctx={ctx} />
					<Faq ctx={ctx} />
					<FinalCta ctx={ctx} />
				</main>
			</div>
			<Footer />
		</>
	);
};

export default AssistantTravelPlannerPage;
